import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { toRefusal } from '@core/helpers/installation-refusal.helper';
import { MeasurementPoint } from '@core/models/installation.model';
import { InstallationDataSourcesService } from '@core/services/installation-data-sources.service';
import {
  DialogShellComponent,
  FilledButtonComponent,
  TextButtonComponent,
  TextFieldComponent,
} from '@custom-components';
import { TranslateModule } from '@ngx-translate/core';
import { PlacedRefusal, RefusalPlace, placeRefusal } from '../../editor-refusal';

export const POINT_NAME_MAX_LENGTH = 255;

export interface PointDialogData {
  installationOid: string;
  point: MeasurementPoint;
}

// Closes with the point as renamed, or with nothing when cancelled.
@Component({
  selector: 'xemex-point-dialog',
  imports: [
    DialogShellComponent,
    FilledButtonComponent,
    FormsModule,
    TextButtonComponent,
    TextFieldComponent,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './point-dialog.component.html',
})
export class PointDialogComponent {
  readonly data = inject<PointDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<MatDialogRef<PointDialogComponent, MeasurementPoint>>(MatDialogRef);
  private readonly service = inject(InstallationDataSourcesService);
  private readonly destroyRef = inject(DestroyRef);

  readonly name = signal(this.data.point.name);
  readonly touched = signal(false);
  readonly isSaving = signal(false);
  readonly refusal = signal<PlacedRefusal | null>(null);
  // Read by the opener once the dialog closes: the point or the installation changed underneath.
  readonly stale = computed(() => this.refusal()?.stale ?? false);
  readonly nameMaxLength = POINT_NAME_MAX_LENGTH;

  readonly nameInvalid = computed(() => {
    const length = this.name().trim().length;
    return length === 0 || length > POINT_NAME_MAX_LENGTH;
  });

  messageAt(place: RefusalPlace): string | null {
    const refusal = this.refusal();
    return refusal?.place === place ? refusal.messageKey : null;
  }

  save(): void {
    this.touched.set(true);
    if (this.nameInvalid()) return;
    this.isSaving.set(true);
    this.refusal.set(null);
    this.service
      .renamePoint(this.data.installationOid, this.data.point.pointOid, {
        name: this.name().trim(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: point => this.dialogRef.close(point),
        error: (error: unknown) => {
          this.isSaving.set(false);
          this.refusal.set(placeRefusal(toRefusal(error)));
        },
      });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
